import { useState, type FormEvent } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { AccountStatus, CustomerAccountSummary } from '@central/contracts';
import { Building2, Pencil, Plus, Search } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import {
  BotaoDeIcone,
  Cabecalho,
  Campo,
  Celula,
  CLASSE_DE_CAMPO,
  Linha,
  LinhaDeCabecalho,
} from '@/features/admin/componentes';
import {
  chavesDeClientes,
  criarCliente,
  listarClientes,
  obterCliente,
  type FiltroDeClientes,
} from '@/features/clientes/clientes.api';
import {
  Contagem,
  DialogoDeEdicaoDeCliente,
  EtiquetaDeSituacao,
  useEscritaDeCliente,
} from '@/features/clientes/componentes';
import {
  enderecoDoCliente,
  SITUACOES_DE_CONTA,
  situacaoDeConta,
} from '@/features/clientes/navegacao';
import { Modal } from '@/features/tickets/Modal';

/**
 * A carteira de clientes.
 *
 * ## Inativos aparecem marcados, não escondidos
 *
 * Um cliente suspenso ou encerrado continua na lista, com a etiqueta dizendo
 * qual dos dois. Esconder criaria o problema clássico desta tela: alguém não
 * acha o cliente, cadastra de novo, e recebe 409 porque o código está ocupado
 * por uma linha que ele não consegue ver — e aí inventa um segundo código para
 * o mesmo cliente, que é exatamente a duplicação que §e.7 mostrou já existir no
 * dado real (FIOTEC e TJMT, dois ids cada).
 *
 * O filtro de situação existe para quem quiser recortar; o padrão é mostrar
 * tudo.
 */
export function ClientesPage() {
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [status, setStatus] = useState<AccountStatus | ''>('');
  const [criando, setCriando] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);

  const filtro: FiltroDeClientes = {
    ...(buscaAplicada ? { q: buscaAplicada } : {}),
    ...(status ? { status } : {}),
  };

  const consulta = useQuery({
    queryKey: chavesDeClientes.lista(filtro),
    queryFn: () => listarClientes(filtro),
    // Sem isto a tabela pisca para o esqueleto a cada troca de filtro, e a
    // pessoa perde a referência de onde estava na lista.
    placeholderData: keepPreviousData,
  });

  const clientes = consulta.data?.items ?? [];
  const filtrando = buscaAplicada !== '' || status !== '';

  return (
    <>
      <div className="mx-auto max-w-[1400px] space-y-4 animate-fade-in">
        <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
          <h1 className="text-base font-semibold">Clientes</h1>
          <Button
            size="sm"
            onClick={() => setCriando(true)}
            icon={<Plus className="h-4 w-4" aria-hidden />}
          >
            Novo cliente
          </Button>
        </header>

        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(evento) => {
            evento.preventDefault();
            setBuscaAplicada(busca.trim());
          }}
        >
          <div className="min-w-[16rem] flex-1 space-y-1.5">
            <label htmlFor="clientes-busca" className="text-sm font-medium">
              Buscar
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                id="clientes-busca"
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Nome, código ou CNPJ"
                className={`${CLASSE_DE_CAMPO} pl-9`}
              />
            </div>
          </div>

          <div className="w-52 space-y-1.5">
            <label htmlFor="clientes-status" className="text-sm font-medium">
              Situação
            </label>
            <select
              id="clientes-status"
              value={status}
              onChange={(evento) => setStatus(evento.target.value as AccountStatus | '')}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">Todas</option>
              {SITUACOES_DE_CONTA.map((situacao) => (
                <option key={situacao.valor} value={situacao.valor}>
                  {situacao.rotulo}
                </option>
              ))}
            </select>
          </div>

          <Button type="submit" variant="secondary" size="md">
            Filtrar
          </Button>
        </form>

        <Card>
          <CardBody className="p-0">
            {consulta.isError ? (
              <div className="px-5 py-6">
                <ErrorState
                  error={consulta.error}
                  title="Não foi possível carregar os clientes"
                  onRetry={() => void consulta.refetch()}
                />
              </div>
            ) : consulta.isPending ? (
              <SkeletonRegion label="Carregando os clientes" className="space-y-3 px-5 py-5">
                <SkeletonText lines={5} />
              </SkeletonRegion>
            ) : clientes.length === 0 ? (
              <div className="px-5 py-12">
                <EmptyState
                  icon={<Building2 className="h-7 w-7" aria-hidden />}
                  title={filtrando ? 'Nenhum cliente com esse filtro' : 'Nenhum cliente cadastrado'}
                  description={
                    filtrando
                      ? 'Limpe a busca e a situação para ver a carteira inteira — inclusive os suspensos e os encerrados.'
                      : 'Cadastre o primeiro cliente, ou promova uma organização que já exista no cadastro.'
                  }
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <LinhaDeCabecalho>
                    <Cabecalho>Cliente</Cabecalho>
                    <Cabecalho>Código</Cabecalho>
                    <Cabecalho>CNPJ</Cabecalho>
                    <Cabecalho>Filiais</Cabecalho>
                    <Cabecalho>Contatos</Cabecalho>
                    <Cabecalho>Chamados</Cabecalho>
                    <Cabecalho>Portal</Cabecalho>
                    <Cabecalho>Situação</Cabecalho>
                    <Cabecalho>{null}</Cabecalho>
                  </LinhaDeCabecalho>
                  <tbody>
                    {clientes.map((cliente) => (
                      <LinhaDeCliente
                        key={cliente.id}
                        cliente={cliente}
                        onEditar={() => setEditandoId(cliente.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <DialogoDeCliente aberto={criando} aoFechar={() => setCriando(false)} />
      <PainelDeEdicao editandoId={editandoId} aoFechar={() => setEditandoId(null)} />
    </>
  );
}

function LinhaDeCliente({
  cliente,
  onEditar,
}: {
  cliente: CustomerAccountSummary;
  onEditar: () => void;
}) {
  const situacao = situacaoDeConta(cliente.status);
  const inativo = cliente.status === 'SUSPENDED' || cliente.status === 'CHURNED';

  return (
    <Linha inativo={inativo}>
      <Celula>
        <Link
          to={enderecoDoCliente(cliente.id)}
          className="font-medium text-foreground hover:text-primary focus-visible:shadow-glow focus-visible:outline-none"
        >
          {cliente.name}
        </Link>
        {cliente.tradeName ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">{cliente.legalName}</span>
        ) : null}
      </Celula>
      <Celula>
        {/* Monoespaçado porque é código: `TJMT` e `TJRO` se confundem em fonte
            proporcional, e é por ele que se conversa sobre o cliente. */}
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{cliente.code}</code>
      </Celula>
      <Celula className="font-mono text-xs text-muted-foreground">{cliente.taxId ?? '—'}</Celula>
      <Celula>
        <Contagem valor={cliente.usage.branches} singular="filial" plural="filiais" />
      </Celula>
      <Celula>
        <Contagem valor={cliente.usage.contacts} singular="contato" plural="contatos" />
      </Celula>
      <Celula>
        <Contagem valor={cliente.usage.tickets} singular="chamado" plural="chamados" />
      </Celula>
      <Celula className="text-xs text-muted-foreground">
        {cliente.portalEnabled ? 'Habilitado' : '—'}
      </Celula>
      <Celula>
        <EtiquetaDeSituacao rotulo={situacao.rotulo} tom={situacao.tom} titulo={situacao.efeito} />
      </Celula>
      <Celula>
        <BotaoDeIcone
          rotulo={`Editar ${cliente.name}`}
          icone={<Pencil className="h-4 w-4" aria-hidden />}
          aoClicar={onEditar}
        />
      </Celula>
    </Linha>
  );
}

function PainelDeEdicao({
  editandoId,
  aoFechar,
}: {
  editandoId: string | null;
  aoFechar: () => void;
}) {
  const consulta = useQuery({
    queryKey: chavesDeClientes.ficha(editandoId ?? ''),
    queryFn: () => obterCliente(editandoId!),
    enabled: editandoId !== null,
  });

  if (!editandoId) return null;

  if (consulta.data) {
    return (
      <DialogoDeEdicaoDeCliente aberto cliente={consulta.data} aoFechar={aoFechar} />
    );
  }

  return (
    <Modal aberto aoFechar={aoFechar} titulo="Carregando…">
      <SkeletonRegion label="Carregando ficha do cliente" className="space-y-3 px-5 py-5">
        <SkeletonText lines={5} />
      </SkeletonRegion>
    </Modal>
  );
}

/**
 * Cadastro de cliente.
 *
 * Só o essencial: o resto — portal, política de vigência, observações — fica
 * na ficha. Um formulário de criação com dezoito campos é um formulário que
 * ninguém termina, e as decisões de portal não se tomam antes de o cliente
 * existir.
 */
function DialogoDeCliente({ aberto, aoFechar }: { aberto: boolean; aoFechar: () => void }) {
  const [codigo, setCodigo] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [dominio, setDominio] = useState('');
  const [status, setStatus] = useState<AccountStatus>('ACTIVE');
  const [semeado, setSemeado] = useState(false);

  if (aberto && !semeado) {
    setSemeado(true);
    setCodigo('');
    setRazaoSocial('');
    setNomeFantasia('');
    setCnpj('');
    setDominio('');
    setStatus('ACTIVE');
  }
  if (!aberto && semeado) setSemeado(false);

  const escrita = useEscritaDeCliente(
    () =>
      criarCliente({
        code: codigo.trim(),
        legalName: razaoSocial.trim(),
        ...(nomeFantasia.trim() ? { tradeName: nomeFantasia.trim() } : {}),
        ...(cnpj.trim() ? { taxId: cnpj.trim() } : {}),
        ...(dominio.trim() ? { domain: dominio.trim().toLowerCase() } : {}),
        status,
      }),
    aoFechar,
  );

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Novo cliente"
      descricao="O cliente nasce como matriz da própria conta. As filiais entram depois, penduradas nele."
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <Campo
          id="cliente-codigo"
          rotulo="Código"
          dica="O código curto pelo qual o cliente já é chamado — APTUM, TJMT, UNIVAG. Se ele veio do Topdesk, use o mesmo: inventar outro dá dois códigos para o mesmo cliente."
        >
          <input
            id="cliente-codigo"
            value={codigo}
            onChange={(evento) => setCodigo(evento.target.value)}
            required
            maxLength={32}
            className={`${CLASSE_DE_CAMPO} font-mono`}
          />
        </Campo>

        <Campo id="cliente-razao" rotulo="Razão social">
          <input
            id="cliente-razao"
            value={razaoSocial}
            onChange={(evento) => setRazaoSocial(evento.target.value)}
            required
            minLength={2}
            maxLength={200}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo id="cliente-fantasia" rotulo="Nome fantasia" opcional>
          <input
            id="cliente-fantasia"
            value={nomeFantasia}
            onChange={(evento) => setNomeFantasia(evento.target.value)}
            maxLength={200}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="cliente-cnpj" rotulo="CNPJ" opcional>
            <input
              id="cliente-cnpj"
              value={cnpj}
              onChange={(evento) => setCnpj(evento.target.value)}
              maxLength={20}
              className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
            />
          </Campo>

          <Campo id="cliente-status" rotulo="Situação">
            <select
              id="cliente-status"
              value={status}
              onChange={(evento) => setStatus(evento.target.value as AccountStatus)}
              className={CLASSE_DE_CAMPO}
            >
              {SITUACOES_DE_CONTA.map((situacao) => (
                <option key={situacao.valor} value={situacao.valor}>
                  {situacao.rotulo}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <Campo
          id="cliente-dominio"
          rotulo="Domínio de e-mail"
          opcional
          dica="Só o domínio: contoso.com.br, sem @ e sem https://. É o que associa o solicitante automaticamente quando o chamado chega pela caixa."
        >
          <input
            id="cliente-dominio"
            value={dominio}
            onChange={(evento) => setDominio(evento.target.value)}
            placeholder="contoso.com.br"
            maxLength={253}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            Criar cliente
          </Button>
        </div>
      </form>
    </Modal>
  );
}
